import Env from "@/config/environment";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef } from "react";
import { ExternalAccountsAPI } from "../api/external-accounts";
import type { ExternalAccount } from "../types";
import { OAUTH_MESSAGE_SOURCE } from "../oauth-message";
import { externalAccountKeys } from "./use-external-accounts";

const origin = (url: string): string | null => {
  try {
    return new URL(url, window.location.origin).origin;
  } catch {
    return null;
  }
};

/**
 * The two pages allowed to report an outcome: the API's callback, and this app's
 * own relay route.
 *
 * Checking this is not ceremony. The redirect URI registered with the provider
 * is deployment configuration, and when production carried **another
 * environment's** URI the consent completed perfectly — on that other
 * environment, which created the account in *its* database and posted
 * `success: true` back here. Everything looked right and the account existed
 * nowhere the user could see it. An outcome from an origin that is not this
 * deployment is the single most useful thing that flow can say.
 */
const trustedOrigins = (): string[] =>
  [origin(Env.server), window.location.origin].filter(
    (value): value is string => !!value,
  );

export interface OAuthPopupResult {
  success: boolean;
  provider?: string;
  /** The account the flow created, when one appeared in the list. */
  accountId?: string;
  error?: string;
}

/** How often we check a popup we heard nothing from. */
const CLOSE_POLL_MS = 500;

/**
 * Open an OAuth consent popup and resolve when it is done.
 *
 * The account is created by the callback *inside the popup*, so the opener has
 * no way of knowing it happened: the connect dialog used to fire and forget,
 * and the new account only ever appeared on a manual reload — the popup closed
 * and, as far as the page was concerned, nothing had happened.
 *
 * Three signals are watched, because none is reliable alone:
 *
 *  - the callback page posts a message before closing — precise, and the only
 *    one that can carry a failure, but it is lost if the browser blocks the
 *    cross-origin post or the user closes the window first;
 *  - the popup being `closed` — always eventually true, but says nothing about
 *    the outcome;
 *  - **the accounts list itself**, which is the only one that can be checked
 *    rather than believed. It settles the second: a window that went away
 *    without a word used to be reported as a success, so a consent the user gave
 *    up on — or one Google refused on its own page, which never redirects back
 *    to us — arrived at the caller as "connected" with nothing connected, and
 *    the setting that asked for the account stayed empty with no reason given.
 *    The list also names the account, which is what the caller has to select.
 *
 * The consent URL may be passed as a promise, and should be: it is fetched from
 * the API, and a `window.open()` running after that `await` has lost the user
 * gesture that allows it — browsers then block the popup, which is the other way
 * "connect an account" ends in nothing happening. The window is therefore opened
 * blank, synchronously, and sent to the provider once the URL lands.
 */
export function useOAuthPopup() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";
  const queryClient = useQueryClient();

  // Cleanup handles kept out of the promise so unmounting can't leave a timer
  // or a listener behind.
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => () => cleanupRef.current?.(), []);

  return useCallback(
    (authUrl: string | Promise<string>): Promise<OAuthPopupResult> => {
      const width = 600;
      const height = 700;
      const left = window.innerWidth / 2 - width / 2;
      const top = window.innerHeight / 2 - height / 2;

      const cached =
        queryClient.getQueryData<ExternalAccount[]>(
          externalAccountKeys.list(clientId),
        ) || [];

      const popup = window.open(
        typeof authUrl === "string" ? authUrl : "about:blank",
        "oauth-popup",
        `width=${width},height=${height},left=${left},top=${top}`,
      );

      if (!popup) {
        return Promise.resolve({
          success: false,
          error: "popup_blocked",
        });
      }

      // What the client already had, read now rather than off the cache alone:
      // an empty cache (the list not fetched yet) would make every existing
      // account look like the one this flow just created. Started after the
      // window is open, so it cannot cost the user gesture.
      const before: Promise<Set<string>> = (
        clientId
          ? ExternalAccountsAPI.getAccounts(clientId).catch(() => cached)
          : Promise.resolve(cached)
      ).then((accounts) => new Set(accounts.map((account) => account.id)));

      /**
       * Re-read the accounts and return the one this flow added, if any. It
       * refreshes the cached list on the way, on failure too: a partial flow can
       * still have written an account, and it belongs on screen either way.
       */
      const connectedAccount = async (): Promise<ExternalAccount | null> => {
        if (!clientId) return null;
        try {
          const known = await before;
          const fresh = await ExternalAccountsAPI.getAccounts(clientId);
          queryClient.setQueryData(externalAccountKeys.list(clientId), fresh);
          return fresh.find((account) => !known.has(account.id)) || null;
        } catch {
          // The list is a second opinion, never the reason a connection fails.
          queryClient.invalidateQueries({
            queryKey: externalAccountKeys.list(clientId),
          });
          return null;
        }
      };

      return new Promise<OAuthPopupResult>((resolve) => {
        let settled = false;
        // Whether the window ever reached the provider. A popup that is gone
        // before it was sent anywhere was never a consent flow.
        let sent = typeof authUrl === "string";

        /** Settle without asking the list — nothing can have been created yet. */
        const fail = (result: OAuthPopupResult) => {
          if (settled) return;
          settled = true;
          cleanup();
          resolve(result);
        };

        /**
         * Settle on an outcome the callback page stated, refreshing the list on
         * the way so the account it created is on screen — and carrying its id,
         * which is what the caller has to select. The message is authoritative:
         * it comes from the page that did the work.
         */
        const finishFromMessage = (result: OAuthPopupResult) => {
          if (settled) return;
          settled = true;
          cleanup();
          void connectedAccount().then((added) =>
            resolve({ ...result, accountId: added?.id }),
          );
        };

        /**
         * Settle a window that went away without a word, on what the accounts
         * list actually holds. This is the case that used to be reported as a
         * success, and the four things it covers are not the same: a consent the
         * user gave up on, a provider that refused on its own page (Google's
         * "access blocked" never redirects back to us), a message the browser
         * dropped, and a connection that worked. Only the list tells them apart.
         */
        const finishFromAccounts = () => {
          if (settled) return;
          settled = true;
          cleanup();
          void connectedAccount().then((added) =>
            resolve(
              added
                ? {
                    success: true,
                    provider: added.provider,
                    accountId: added.id,
                  }
                : { success: false, error: "closed_before_completion" },
            ),
          );
        };

        const onMessage = (event: MessageEvent) => {
          const data = event.data;
          if (!data || data.source !== OAUTH_MESSAGE_SOURCE) return;

          // A callback page answering from somewhere else entirely finished a
          // connection in another deployment's database; nothing it says applies
          // here. Naming the origin is the whole diagnosis, so it is reported
          // rather than ignored.
          if (!trustedOrigins().includes(event.origin)) {
            fail({
              success: false,
              error: `oauth_wrong_origin:${event.origin}`,
            });
            return;
          }

          finishFromMessage({
            success: !!data.success,
            provider: data.provider,
            error: data.error,
          });
        };

        const timer = window.setInterval(() => {
          if (!popup.closed) return;
          // No message reached us, so the flow is over one way or another and
          // the list has to say which — unless the window went away before it
          // was even sent to the provider, which browsers do to a popup they
          // disown: there is nothing to look for.
          if (!sent) return fail({ success: false, error: "popup_blocked" });
          finishFromAccounts();
        }, CLOSE_POLL_MS);

        const cleanup = () => {
          window.clearInterval(timer);
          window.removeEventListener("message", onMessage);
          cleanupRef.current = null;
        };

        cleanupRef.current = cleanup;
        window.addEventListener("message", onMessage);

        if (typeof authUrl !== "string") {
          authUrl
            .then((url) => {
              if (settled) return;
              try {
                popup.location.href = url;
                sent = true;
              } catch {
                // Cross-origin hardening can disown the window we just opened;
                // nothing can be navigated then, so say so rather than leave a
                // blank popup sitting there.
                popup.close();
                fail({ success: false, error: "popup_blocked" });
              }
            })
            .catch((error: unknown) => {
              popup.close();
              fail({
                success: false,
                error:
                  error instanceof Error ? error.message : "oauth_url_failed",
              });
            });
        }
      });
    },
    [clientId, queryClient],
  );
}
